import cn from 'classnames';
import moment from 'moment';
import { useEffect, useState, useMemo, useContext, useCallback } from 'react';
import Skeleton from 'react-loading-skeleton';

import DefaultLink from 'components/common/DefaultLink';
import Heading from 'components/common/Heading';
import Text from 'components/common/Text';
import { UserContext, VoucherContext } from 'components/signUpFunnel/Contexts';
import OrderSummary from 'components/signUpFunnel/pickMeals/orderSummary/OrderSummary';
import localStorage from 'helpers/localStorage';
import useAnalyticsEffect from 'hooks/useAnalyticsEffect';
import useScreenSizeConstants from 'hooks/useScreenSizeConstants';
import {
  HIGH_PROTEIN,
  CALORIE_CONSCIOUS,
  FIBER_RICH,
  textTags,
  TEMPO,
  CARB_CONSCIOUS,
  GLP_SMART_NAME,
  STANDARD_MEAL_SERVING_SIZE,
  PICK_YOUR_MEALS_MINIMUM_ITEMS,
} from 'lib/constants';
import { SHOW_PROMO_BANNER_LOCAL_STORAGE } from 'lib/constants/signUp';
import * as ErrorLogging from 'services/ErrorLogging';
import RequestService from 'services/RequestService';

import MealCardContainer from './MealCardContainer';
import PickMealsBanner from './PickMealsBanner';
import PickMealsFilters from './PickMealsFilters';

const MEAL_FILTERS = [HIGH_PROTEIN, CALORIE_CONSCIOUS, CARB_CONSCIOUS, FIBER_RICH, GLP_SMART_NAME];

const MealCardSkeleton = () => {
  return (
    <div className="flex flex-wrap justify-between gap-2">
      <div className="relative m-2 flex h-96 w-96 animate-pulse rounded border border-solid border-charcoal-60 bg-charcoal-50 shadow-md"></div>
    </div>
  );
};

const MealCardContainerSkeleton = () => (
  <div className="mx-auto max-w-lg p-6 bp1:max-w-6xl" data-testid="loading-skeleton">
    <div className="px-2">
      <Skeleton count={2} height={40} className="my-1" />
    </div>
    <div className="grid grid-cols-1 bp2:grid-cols-2 bp3:grid-cols-3">
      {Array.from({ length: 6 }, (_, index) => (
        <MealCardSkeleton key={index} />
      ))}
    </div>
  </div>
);

export const formatShipWeek = dateString => {
  const startDate = moment(dateString);
  const startMonth = startDate.format('MMMM');
  const startDay = startDate.format('Do');

  return `${startMonth} ${startDay}`;
};

const MenuContainer = () => {
  const [filteredMeals, setFilteredMeals] = useState(null);
  const [shipWeek, setShipWeek] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [totalServings, setTotalServings] = useState(0);
  const [selectedMeals, setSelectedMeals] = useState([]);
  const [meals, setMeals] = useState(null);
  const [menuId, setMenuId] = useState();
  const [selectedMenuClosed, setSelectedMenuClosed] = useState(false);
  const [orderSummary, setOrderSummary] = useState({});
  const [isOrderSummaryOpen, setIsOrderSummaryOpen] = useState(false);
  const [showUclBanner, setShowUclBanner] = useState(false);
  const { user } = useContext(UserContext);
  const { voucher } = useContext(VoucherContext);
  const { isMobile, isTablet } = useScreenSizeConstants();

  const toggleOrderSummary = () => {
    setIsOrderSummaryOpen(!isOrderSummaryOpen);
  };

  const Header = () => {
    return (
      <section className={!isMobile ? 'p-3' : 'p-4'}>
        <Heading
          brandStylesOverrides={cn('text-tempo-black-100', !isMobile ? 'h1' : 'h4 text-center')}
        >
          Pick Your Meals
        </Heading>
      </section>
    );
  };

  useAnalyticsEffect(({ events, logEvent }) => {
    logEvent(events.pickMeals[TEMPO].pickMeals.viewed, {
      mealPlanSlug: TEMPO,
    });
  }, []);

  useEffect(() => {
    const getPickMealsMenu = async () => {
      setIsLoading(true);
      try {
        const response = await RequestService.getPickMealsMenu();
        if (response.ok) {
          setMenuId(response.id);
          setMeals(response.meals);
          setFilteredMeals(response.meals);
          setShipWeek(formatShipWeek(response.mondayOfShipWeek));
          setIsLoading(false);
        }
      } catch (error) {
        ErrorLogging.captureException(error.message);
      }
    };

    getPickMealsMenu();
  }, []);

  useEffect(() => {
    const getOrderSummary = async () => {
      try {
        const response = await RequestService.getProjectedCosts({
          params: {
            weeklyBasket: {
              numberOfMeals: user.weeklyMeals,
              mealPlanSlug: user.mealPlanSlug,
              servings: user.servings,
              voucherCode: voucher?.code,
            },
          },
        });
        if (response.ok) {
          setOrderSummary(response.weeklyBasket);
        }
      } catch (error) {
        ErrorLogging.captureException(error.message);
      }
    };

    if (user) getOrderSummary();
  }, [voucher?.code, user]);

  useEffect(() => {
    const fetchBasket = async () => {
      try {
        const response = await RequestService.getPickMealsBasket({ params: { menuId } });
        if (response.ok && response?.basket?.meals && Array.isArray(response.basket.meals)) {
          const hasPreviousMeals = response.basket.meals.length > 0;
          setSelectedMeals(response.basket.meals);
          setTotalServings(response.basket.meals.reduce((total, meal) => total + meal.servings, 0));
          setSelectedMenuClosed(response.basket.selectedMenuClosed);

          const promoBannerActive = localStorage.read(SHOW_PROMO_BANNER_LOCAL_STORAGE);

          if (hasPreviousMeals && !promoBannerActive) {
            setShowUclBanner(true);
          } else {
            setShowUclBanner(false);
          }
        } else {
          setSelectedMeals([]);
          setTotalServings(0);
          setShowUclBanner(false);
        }
      } catch (error) {
        setSelectedMeals([]);
        setTotalServings(0);
        ErrorLogging.captureException(error.message);
      }
    };

    if (menuId) fetchBasket();
  }, [menuId]);

  const addServing = useCallback(({ id, photoUrl, title }) => {
    setTotalServings(prevTotalServings => prevTotalServings + STANDARD_MEAL_SERVING_SIZE);
    setSelectedMeals(prevMealServings => {
      const mealExists = prevMealServings.some(meal => meal.id === id);

      if (mealExists) {
        return prevMealServings.map(meal =>
          meal.id === id
            ? {
                ...meal,
                servings: meal.servings + STANDARD_MEAL_SERVING_SIZE,
                photoUrl: meal.photoUrl || photoUrl,
                title: meal.title || title,
              }
            : meal
        );
      } else {
        return [...prevMealServings, { id, servings: STANDARD_MEAL_SERVING_SIZE, photoUrl, title }];
      }
    });
  }, []);

  const removeServing = useCallback(id => {
    setTotalServings(prevTotalServings => prevTotalServings - STANDARD_MEAL_SERVING_SIZE);
    setSelectedMeals(prevMealServings =>
      prevMealServings
        .map(meal =>
          meal.id === id ? { ...meal, servings: meal.servings - STANDARD_MEAL_SERVING_SIZE } : meal
        )
        .filter(meal => meal.servings > 0)
    );
  }, []);

  const removeMeal = useCallback(({ id, servings }) => {
    setTotalServings(prevTotalServings => prevTotalServings - servings);
    setSelectedMeals(prevMealServingsSelected => {
      return prevMealServingsSelected.filter(meal => meal.id !== id);
    });
  }, []);

  const isBoxSizeReached = useMemo(() => {
    if (!user?.weeklyMeals) return false;

    const userMaxServings = user.weeklyMeals * STANDARD_MEAL_SERVING_SIZE;
    if (userMaxServings < PICK_YOUR_MEALS_MINIMUM_ITEMS) return false;

    return totalServings >= userMaxServings;
  }, [user?.weeklyMeals, totalServings]);

  const handleFilterChange = filter => {
    if (!filter) {
      setFilteredMeals(meals);
      return;
    }

    const filterLabel = textTags(true)[filter]?.text;
    const filtered = meals.filter(meal => meal.mealLabels.includes(filterLabel));
    setFilteredMeals(filtered);
  };

  const displayableFilters = useMemo(() => {
    if (meals && textTags(true) && MEAL_FILTERS) {
      return MEAL_FILTERS.filter(filter =>
        meals.some(meal => {
          const filterLabel = textTags(true)[filter]?.text;
          const hasMealLabel = meal.mealLabels.includes(filterLabel);
          return hasMealLabel;
        })
      );
    } else {
      return [];
    }
  }, [meals]);

  return (
    <>
      {user && !isOrderSummaryOpen && showUclBanner && (
        <PickMealsBanner selectedMenuClosed={selectedMenuClosed} />
      )}
      <div className="w-full overflow-x-auto">
        <div className={!isMobile ? 'flex flex-row' : ''}>
          <div className="flex-1 bg-tempo-gray-40">
            <div className="mx-auto w-full max-w-lg p-4 bp3:max-w-3xl bp4:max-w-5xl">
              <Header />
              {isLoading ? (
                <MealCardContainerSkeleton />
              ) : (
                <>
                  <PickMealsFilters
                    handleFilterChange={handleFilterChange}
                    displayableFilters={displayableFilters}
                  />
                  {(isMobile || isTablet) && (
                    <div className="flex items-center justify-between bg-tempo-gray-40 px-4 pb-0 pt-4">
                      <Text className="justify-center rounded-3xl bg-tempo-gray-100 px-3 py-2 font-medium text-tempo-black-100">
                        {user?.weeklyMeals} Weekly Meals
                      </Text>
                      <DefaultLink
                        to="/our-menu?pym=true&box=true"
                        isExternal
                        className="textMd py-3 text-tempo-blue-90"
                      >
                        Change Box Size
                      </DefaultLink>
                    </div>
                  )}
                  <div className="p-4 bp3:p-6">
                    <MealCardContainer
                      meals={filteredMeals}
                      selectedMeals={selectedMeals}
                      shipWeek={shipWeek}
                      addServing={addServing}
                      removeMeal={removeMeal}
                      removeServing={removeServing}
                      disabled={isBoxSizeReached}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="flex-col bp2:flex bp2:min-w-[375px] bp2:border-l bp2:border-solid bp2:border-charcoal-60">
            {user && !isLoading && meals && (
              <OrderSummary
                isOrderSummaryOpen={isOrderSummaryOpen}
                toggleOrderSummary={toggleOrderSummary}
                orderSummary={orderSummary}
                selectedMeals={selectedMeals}
                totalServings={totalServings}
                meals={meals}
                menuId={menuId}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

MenuContainer.propTypes = {
  isOrderSummaryOpen: PropTypes.bool,
};

export default MenuContainer;
